import * as THREE from "three";
import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { vectors, lookAtDirections } from "./constants";
import { roomData, roomPoints } from "./rooms";
import { initWelcomePopup } from "./others/WelcomePopup";
import { TouchControls, isMobileDevice } from "./others/TouchControls";
import * as helpers from "./helpers";
import "./style.css";

// Current Camera
let currentCamera = null;

// FreeRoam Movement Booleans
let moveForward = false,
  moveBackward = false,
  moveRight = false,
  moveLeft = false,
  isSprinting = false;

//Cooldown control
let lastLockTime = 0;

//Cinematic camera flag
let cinematicCameraInitialized = false;

// Frustum Culling & Distance Culling
const frustum = new THREE.Frustum();
const cameraViewProjectionMatrix = new THREE.Matrix4();
let allMeshes = [];
const cullDistance = 30; // Hide the meshes after 30 br in the model
let lastCullTime = 0;
const cullInterval = 200; // Control the movements in every 200ms

//Stair Variables
const groundLevel = 0.0;        // Ground Level
const groundOffset = 0.3;       // The height of the camera to the ground level
const maxStepHeight = 0.5;      // Max height of steps
const groundFollowSpeed = 12.0;  // Adhesion rate to the surface (lerp coefficient)
const slopeLimit = 0.5;         // Max inclination (cos angle)

const raycaster = new THREE.Raycaster();
let collisionMeshes = [];

const downRaycaster = new THREE.Raycaster();
let currentGroundY = groundLevel; // Instant floor height

// Time for accelaration
let prevTime = performance.now();

// For Accelaration
const velocity = new THREE.Vector3();
const direction = new THREE.Vector3();

// For cinematic view
let isCinematic = false;
let clock = new THREE.Clock();
const cinematicSpeed = 2.0;
let cinematicDuration = 0;
let lookAtObject = null,
  cinematicPath = null;
let pathTarget = new THREE.Vector3(0, 0, 0);
let destinationNodeId = null; // Store destination node ID for lookAt direction


// Left bottom panel for cinematic selection
const bottomPanel = document.querySelector(".leftBottomPanel");

// Canvas
const canvas = document.querySelector("canvas.webgl");

//Mobile Detection
const isMobile = isMobileDevice();

// Scene
const scene = new THREE.Scene();

// GLTF Loader --Maya Model
const myModelLoader = new GLTFLoader();
myModelLoader.load("./assets/ikinciKat_08.glb", function (glb) {
  glb.parser.getDependencies("material").then((materials) => {
    materials.forEach((material) => (material.depthWrite = true));
  });

  glb.scene.position.set(0, 0, 0);
  glb.scene.scale.set(1, 1, 1);

  glb.scene.traverse((child) => {
    if (child.isMesh) {
      child.frustumCulled = true;
      child.geometry.computeBoundingSphere();
      child.geometry.computeBoundingBox();

      if (child.name.toLowerCase().includes("_collision")) {
        // Hide the wall
        child.visible = false; 

        // Add the invisible wall to the collision meshes
        collisionMeshes.push(child); 
      } else {
        child.visible = true; 
        allMeshes.push(child);
        collisionMeshes.push(child);
      }
    }
  });

  scene.add(glb.scene);
});

// AmbientLight
const ambientLight = new THREE.AmbientLight(0xffffff, 1);
scene.add(ambientLight);

// Directional Light
const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
directionalLight.position.set(20, 50, 20);
directionalLight.castShadow = true;
scene.add(directionalLight);

/**
 * Sizes
 */
function updateOrientationUI() {
  if (!isMobile) return;

  const html = document.documentElement;
  const shortSide = Math.min(window.innerWidth, window.innerHeight);
  html.classList.add("mobile-ui");
  html.classList.toggle("compact-ui", shortSide <= 380); 
}

function getViewportSize() {
  return { width: window.innerWidth, height: window.innerHeight };
}

updateOrientationUI();

const sizes = getViewportSize();

const updateOnResize = () => {
  updateOrientationUI();

  // Update sizes
  const newSizes = getViewportSize();
  sizes.width = newSizes.width;
  sizes.height = newSizes.height;

  // Update current camera (update whichever camera is active)
  if (currentCamera) {
    currentCamera.aspect = sizes.width / sizes.height;
    currentCamera.updateProjectionMatrix();
  }

  freeRoamCamera.aspect = sizes.width / sizes.height;
  freeRoamCamera.updateProjectionMatrix();

  cinematicCamera.aspect = sizes.width / sizes.height;
  cinematicCamera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setClearColor(0xccccff);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
};

// Free Roam Camera
const freeRoamCamera = new THREE.PerspectiveCamera(
 75,
 sizes.width / sizes.height,
 0.01,
 1000
);
const { x, y, z } = vectors[0][1];
freeRoamCamera.position.set(-12.3, groundLevel, 4.8); //Start the model in front of the department building
freeRoamCamera.rotation.set(0, 4.5, 0);
freeRoamCamera.lookAt(freeRoamCamera.position.clone().add(new THREE.Vector3(0.0, -0.0, -1.0)) //start lookAt position
); //Set the model's viewing direction

// Cinematic Camera
const cinematicCamera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.01,
  1000
);
cinematicCamera.position.set(-12.3, groundLevel, 4.8);
cinematicCamera.rotation.set(0, 4.5, 0);
cinematicCamera.lookAt(cinematicCamera.position.clone().add(new THREE.Vector3(0.0, -0.0, -1.0)));

// This controls provide hiding cursor and event binding to the
// First parameter (camera)
const controls = new PointerLockControls(freeRoamCamera, document.body);
scene.add(controls.getObject());

const touchControls = new TouchControls(freeRoamCamera, {
  onTap: () => handleFreeRoamInteraction(),
});

// PointerLockControls kilitli veya mobilde touch kontrol aktifse serbest gezintiyi aktif say
function isFreeRoamActive() {
  return controls.isLocked || (isMobile && touchControls.enabled);
}

controls.pointerSpeed = 1.0;

/*
 * FreeRoam Management
 * Key Events
 */
const onKeyDown = function (event) {
  switch (event.keyCode) {
    case 87: // w - move forward
      moveForward = true;
      break;
    case 65: // a - move left
      moveLeft = true;
      break;
    case 83: // s - move back
      moveBackward = true;
      break;
    case 68: // d - move right
      moveRight = true;
      break;
    case 16: // Shift - sprint
      isSprinting = true;
      break;
  }
};

const onKeyUp = function (event) {
  switch (event.keyCode) {
    case 87: // w
      moveForward = false;
      break;
    case 65: // a
      moveLeft = false;
      break;
    case 83: // s
      moveBackward = false;
      break;
    case 68: // d
      moveRight = false;
      break;
    case 16: // Shift
      isSprinting = false;
      break;
  }
};

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  alpha: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

function playDingSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
    gain1.gain.setValueAtTime(0.35, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.35);
  } catch(e) {}
}

function shakeInfoBox() {
  const infoBox = document.getElementById('infoBox');
  if (!infoBox) return;
  playDingSound();
  // Remove fadein so it never replays, remove shake to restart it
  infoBox.classList.remove('fadein');
  infoBox.classList.remove('shake');
  void infoBox.offsetWidth;
  infoBox.classList.add('shake');
  infoBox.addEventListener('animationend', () => {
    infoBox.classList.remove('shake');
  }, { once: true });
}

function isInfoBoxOpen() {
  const infoBox = document.getElementById('infoBox');
  return infoBox && !infoBox.classList.contains('hidden');
}

function changeToFreeRoamCamera() {
  if (isInfoBoxOpen()) { shakeInfoBox(); return; }
  const now = Date.now();

  if(now - lastLockTime <1500){ return; } 
  //If current camera is already on free roam camera, lock the controls
  if (currentCamera === freeRoamCamera) {
    if (isMobile) {
      touchControls.enable();
      lastLockTime = now;
    } else if (!controls.isLocked) {
      try {
        controls.lock();
        lastLockTime = now;
      } catch (error) {
        console.error("Kilitleme Hatası:", error);
      }
    }
    return;
  }

  cinematicCameraInitialized = false;
  
  //Get cinematic camera's last position and lookAt direction
  const cinematicPos = cinematicCamera.position.clone();
  const cinematicDirection = new THREE.Vector3();
  cinematicCamera.getWorldDirection(cinematicDirection);

  //Move freeRoamCamera to cinematicCamera's position
  freeRoamCamera.position.copy(cinematicPos);

  //Get controls object to the same position
  controls.getObject().position.copy(cinematicPos);

  //Set the lookAt direction
  const lookAtPoint = cinematicPos.clone().add(cinematicDirection.multiplyScalar(10));
  freeRoamCamera.lookAt(lookAtPoint);

  currentCamera = freeRoamCamera;

  if (isCinematic) {
    stopCinematicTrip();
  }

  if (isMobile) {
    touchControls.enable();
    lastLockTime = now;
  } else {
    try {
      controls.lock();
      lastLockTime = now;
    } catch (error) {
      console.error("İlk geçişte kilitleme hatası:", error);
    }
  }

  if (!bottomPanel.classList.contains("hide")) {
    bottomPanel.classList.add("hide");
  }

  document.getElementById('changeToFreeRoamCamera').classList.add('active');
  document.getElementById('changeToCinematicCamera').classList.remove('active');
}

function changeToCinematicCamera() {
  if (isInfoBoxOpen()) { shakeInfoBox(); return; }
  controls.unlock();
  if (isMobile) touchControls.disable();
  currentCamera = cinematicCamera;

  //cinematic camera's position is synchronized only once
  if(!cinematicCameraInitialized) {
    //Take the free roam camera's position and direction
    const currentPos = freeRoamCamera.position.clone();
    const currentDirection = new THREE.Vector3();
    controls.getDirection(currentDirection);

    //Set up the cinematic camera to free roam camera's position and direction
    cinematicCamera.position.copy(currentPos);
    cinematicCamera.lookAt(currentPos.clone().add(currentDirection));

    cinematicCameraInitialized = true;
  }

  if (bottomPanel.classList.contains("hide")) {
    bottomPanel.classList.remove("hide");
  }

  document.getElementById('changeToCinematicCamera').classList.add('active');
  document.getElementById('changeToFreeRoamCamera').classList.remove('active');
}

function startCinematicViewClock() {
  clock.start();
}

function stopCinematicViewClock() {
  clock.stop();
}

//Find the nearest Node for cinematic trip
function findNearestNode(position, excludeNode = null) {
  let nearestNode = null;
  let minDist = Infinity;
  
  vectors.forEach((nodeObj) => {
    const [nodeName, coordinate] = Object.entries(nodeObj)[0];
    
    // Skip the destination node
    if (nodeName === excludeNode) {
      return;
    }
    
    const distance = position.distanceTo(coordinate);

    if (distance < minDist) {
      minDist = distance;
      nearestNode = nodeName;
    }
  });

  return {node: nearestNode, distance: minDist};
}

function startCinematicTrip() {

  if (isInfoBoxOpen()) { shakeInfoBox(); return; }

  // Get selections
  /*const sourceFloorSelect = document.querySelector("#sourceFloorSelection");
  const sourceSelect = document.querySelector("#sourceSelection");*/
  const sourceFloorSelect = document.querySelector("#sourceFloorSelection");
  const sourceSelect = document.querySelector("#sourceSelection");
  const destinationSelect = document.querySelector("#destinationSelection");
  
  const sourceFloor = sourceFloorSelect.value;
  let source = sourceFloor === "current" ? "current" : sourceSelect.value;
  const destination = destinationSelect.value;

  if (!destination || destination === "0") {
    alert("Lütfen hedef nokta seçiniz!");
    return;
  }

  // Store destination node ID for lookAt direction
  destinationNodeId = destination;

  // If the source is "current" find the nearest node from the current camera position
  if (source === "current") {
    const currentPos = cinematicCamera.position.clone();
    const {node: nearestNode} = findNearestNode(currentPos,null);

    source = nearestNode;
    console.log(`Starting from nearest node: ${source}`);
    console.log("Current camera position:", currentPos);
    console.log("Source:", source, "Destination:", destination);
  }

  const shortestWayArray = helpers.getShortestWayPointsArray(source, destination);

  if (!shortestWayArray || shortestWayArray.length === 0) {
    alert(`${source} noktasından ${destination} noktasına giden yol bulunamadı!`);
    return;
  }

  // Start by maintaining the current camera position and view direction
  if (sourceSelect.value === "current") {
    // Add the current camera position 
    const currentCameraPos = cinematicCamera.position.clone();
    currentCameraPos.y = groundLevel; // maintain ground level 
    shortestWayArray.unshift(currentCameraPos);
  } else if (source === destination && shortestWayArray.length === 1) {
    const dummyPoint = shortestWayArray[0].clone().add(new THREE.Vector3(0.01, 0, 0.01));
    shortestWayArray.unshift(dummyPoint);
  }

  const curve = helpers.createCurve(shortestWayArray);

  // Set the point of view to the direction of progress
  // Create a lookAt object for the last coordinate 
  const lastPoint = shortestWayArray[shortestWayArray.length - 1];

  // Create lookAt object using the destination node's lookAt direction if available
  if (lookAtDirections[destination]) {
    const lookAtDir = lookAtDirections[destination];
    const lookAtPoint = lastPoint.clone().add(lookAtDir.clone().multiplyScalar(10));
    lookAtObject = helpers.createLookAtObject(lookAtPoint);
  } else {
    // Fallback to default behavior
    lookAtObject = helpers.createLookAtObject(lastPoint);
  }

  cinematicPath = curve;
  const pathLength = cinematicPath.getLength();
  const calculatedDuration = pathLength / cinematicSpeed;

  const minCinematicDuration = 2.5; 
  cinematicDuration = Math.max(calculatedDuration, minCinematicDuration);

  startCinematicViewClock();
  isCinematic = true;

  const startBtn = document.getElementById('startCinematicTrip');
  if (startBtn) {
    startBtn.disabled = true; 
    startBtn.classList.add('in-progress'); 
    startBtn.innerHTML = `
      Hedefe gidiliyor...
    `;
  }
}

function stopCinematicTrip() {
  const sourceSelect = document.querySelector("#sourceSelection");
  const destinationSelect = document.querySelector("#destinationSelection");

  // Reset selections
  if (sourceSelect) sourceSelect.value = "current";
  if (destinationSelect) destinationSelect.value = "0";

  // Reset variables for path
  lookAtObject = null;
  cinematicPath = null;
  destinationNodeId = null;

  // Reset clock and create another clock
  stopCinematicViewClock();
  clock = new THREE.Clock();
  
  isCinematic = false;
  
  console.log("Cinematic trip stopped at destination");

  const startBtn = document.getElementById('startCinematicTrip');
  if (startBtn) {
    startBtn.disabled = false; // Butonu tekrar tıklanabilir yap
    startBtn.classList.remove('in-progress'); // CSS class'ını kaldır
    // Butonun orijinal içeriğini geri yükle
    startBtn.innerHTML = `
      <svg class="route-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"></polygon>
      </svg>
      Rotayı Başlat
    `;
  }
}

/**
 * Animate
 */
function animate() {
  requestAnimationFrame(animate);

  const time = performance.now();

  // FreeRoam Camera Logic
  if (isFreeRoamActive()) {
    const delta = (time - prevTime) / 1000;
    const object = controls.getObject();

    const oldPosition = object.position.clone();

    const touchMove = isMobile ? touchControls.getMoveVector() : { forward: 0, right: 0, sprint: false };
    const sprintMultiplier = (isSprinting || touchMove.sprint) ? 2.5 : 1.0;

    velocity.x -= velocity.x * 10.0 * delta;
    velocity.z -= velocity.z * 10.0 * delta;

    direction.z = Number(moveForward) - Number(moveBackward) + touchMove.forward;
    direction.x = Number(moveRight) - Number(moveLeft) + touchMove.right;
    direction.normalize();

    const hasKeyboardInput = moveForward || moveBackward || moveLeft || moveRight;
    const hasTouchInput = touchMove.forward !== 0 || touchMove.right !== 0;

    if (hasKeyboardInput || hasTouchInput) {
      if (direction.z !== 0) velocity.z -= direction.z * 10 * sprintMultiplier * delta;
      if (direction.x !== 0) velocity.x -= direction.x * 10 * sprintMultiplier * delta;
    }

    controls.moveRight(-velocity.x * delta);
    controls.moveForward(-velocity.z * delta);
    
    // Collision control - 4 way short distance
    const posCol = object.position;
    let hasCollision = false;
    const wallDistance = 0.10; // Min distance to the wall
    
    const checkDirections = [
      new THREE.Vector3(1, 0, 0),   // right
      new THREE.Vector3(-1, 0, 0),  // left
      new THREE.Vector3(0, 0, 1),   // front
      new THREE.Vector3(0, 0, -1)   // back
    ];
    
    for (let dir of checkDirections) {
      raycaster.set(posCol, dir);
      const intersects = raycaster.intersectObjects(collisionMeshes, true);
      
      if (intersects.length > 0 && intersects[0].distance < wallDistance) {
        const normal = intersects[0].face.normal.clone();
        normal.transformDirection(intersects[0].object.matrixWorld);

        if (normal.y < slopeLimit) {
          hasCollision = true;
          break;
        }
      }
    }
    
    //If there is collision, go back to the old position
    if (hasCollision) {
      object.position.copy(oldPosition);
    }

    //  Stair System
    const castOrigin = object.position.clone();
    castOrigin.y += maxStepHeight + 0.1; 

    downRaycaster.set(castOrigin, new THREE.Vector3(0, -1, 0));
    const groundHits = downRaycaster.intersectObjects(collisionMeshes, true);

    let validGroundHit = null;

    // Control all the grounding hits until finding the real ground
    for (let i = 0; i < groundHits.length; i++) {
      const hit = groundHits[i];
      const hitNormal = hit.face.normal.clone();
      
      hitNormal.transformDirection(hit.object.matrixWorld);
      
      // slope control
      if (hitNormal.y > slopeLimit) {
        const targetY = hit.point.y + groundOffset;
        const heightDiff = targetY - object.position.y;
        
        if (heightDiff <= maxStepHeight) {
          validGroundHit = hit;
          break; 
        }
      }
    }

    if (validGroundHit) {
      const targetY = validGroundHit.point.y + groundOffset;
      const heightDiff = targetY - object.position.y;
      
      //lerp control
      if (heightDiff >= -2.0) {
        currentGroundY = THREE.MathUtils.lerp(
          object.position.y,
          targetY,
          groundFollowSpeed * delta
        );
        object.position.y = currentGroundY;
      }
    } else {
      // If there is no valid ground, take the minimum level of the ground
      object.position.y = THREE.MathUtils.lerp(
        object.position.y,
        groundLevel,
        groundFollowSpeed * delta
      );
    }  
  }

  // Cinematic camera system

  if (isCinematic && cinematicPath) {
    const elapsed = clock.getElapsedTime();
    let t = elapsed / cinematicDuration;

    if (t >= 1.0) {
      t = 1.0;
      cinematicPath.getPointAt(1.0, pathTarget);
      cinematicCamera.position.copy(pathTarget);

      if (destinationNodeId && lookAtDirections[destinationNodeId]) {
        const lookAtDir = lookAtDirections[destinationNodeId];
        const finalLookAtPoint = pathTarget.clone().add(lookAtDir.clone().multiplyScalar(10));

        const rotationTime = elapsed - cinematicDuration;
        const rotationDuration = 2.0;
        const rotationProgress = Math.min(rotationTime / rotationDuration, 1.0);

        // With Ease-in-out smooth rotation
        const easedProgress = rotationProgress < 0.5
          ? 2 * rotationProgress * rotationProgress
          : -1 + (4 - 2 * rotationProgress) * rotationProgress;

        if (rotationProgress < 1.0) {
          const currentDir = new THREE.Vector3();
          cinematicCamera.getWorldDirection(currentDir);
          const currentLookAt = pathTarget.clone().add(currentDir.multiplyScalar(10));
          const interpolatedLookAt = new THREE.Vector3().lerpVectors(
            currentLookAt, finalLookAtPoint, easedProgress
          );
          cinematicCamera.lookAt(interpolatedLookAt);
        } else {
          cinematicCamera.lookAt(finalLookAtPoint);
          stopCinematicTrip();
        }
      } else if (lookAtObject) {
        cinematicCamera.lookAt(lookAtObject.position);
        stopCinematicTrip();
      } else {
        stopCinematicTrip();
      }

    } else {
      cinematicPath.getPointAt(t, pathTarget);
      cinematicCamera.position.copy(pathTarget);

      const lookAheadT = Math.min(t + 0.04, 1.0); 
      const lookAheadPoint = new THREE.Vector3();
      cinematicPath.getPointAt(lookAheadT, lookAheadPoint);

      const targetDir = lookAheadPoint.clone().sub(pathTarget).normalize();

      if (targetDir.lengthSq() > 0.001) {
        const currentDir = new THREE.Vector3();
        cinematicCamera.getWorldDirection(currentDir);

        //Adaptive lerp 
        const angleDiff = currentDir.angleTo(targetDir); 
        const lerpFactor = THREE.MathUtils.clamp(
          0.08 + angleDiff * 0.15,
          0.06,
          0.25
        );

        currentDir.lerp(targetDir, lerpFactor);
        cinematicCamera.lookAt(pathTarget.clone().add(currentDir.multiplyScalar(3)));
      }
    }
  }

  // Update prevtime
  prevTime = time;

  // Distance culling
  const now = performance.now();
  if (allMeshes.length > 0 && now - lastCullTime > cullInterval) {
  lastCullTime = now;

  // Update Frustum matrix
  currentCamera.updateMatrixWorld();
  cameraViewProjectionMatrix.multiplyMatrices(
    currentCamera.projectionMatrix,
    currentCamera.matrixWorldInverse
  );
  frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

  const camPos = currentCamera.position;

  allMeshes.forEach((mesh) => {
    // Calculate the distance to the camera
    const dist = camPos.distanceTo(mesh.getWorldPosition(new THREE.Vector3()));

    if (dist > cullDistance) {
      //hide
      mesh.visible = false;
    } else {
      // Control if it's in the frustum
      mesh.visible = frustum.intersectsObject(mesh);
    }
  });
}

  // Renders
  checkIconHover();
  renderer.render(scene, currentCamera);
}


// Create Clickable Points --Popup System 
// "ⓘ" ikonu için canvas texture oluştur
function createInfoIconTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Arka plan şeffaf
  ctx.clearRect(0, 0, size, size);

  // Dış daire (beyaz dolgu, koyu kenarlık)
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 1)';
  ctx.fill();
  ctx.strokeStyle = '#1a6fbf';
  ctx.lineWidth = 14;
  ctx.stroke();

  // "i" harfi
  ctx.fillStyle = '#1a6fbf';
  ctx.font = `bold ${size * 0.52}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('i', size / 2, size / 2 + size * 0.04);

  return new THREE.CanvasTexture(canvas);
}

const infoIconTexture = createInfoIconTexture();

// Tıklanabilir noktalar oluşturma 
function createClickablePoint(position, roomId, rotationY = Math.PI / 2) {
  const size = isMobile ? 0.16 : 0.1;
  const geometry = new THREE.PlaneGeometry(size, size); 
  const material = new THREE.MeshBasicMaterial({
    map: infoIconTexture,
    transparent: true,
    depthTest: true,
    side: THREE.DoubleSide,
  });
  const plane = new THREE.Mesh(geometry, material);
  plane.position.set(position.x, position.y, position.z);
  
  // Sabit değer yerine odanın özel rotasyon değerini atıyoruz
  plane.rotation.y = rotationY;
  
  plane.userData = { roomId: roomId };
  scene.add(plane);
  return plane;
}

// Create automatically from the roomPoints in rooms.js
const clickableObjects = roomPoints.map(({ roomId, position, rotationY }) =>
  createClickablePoint(position, roomId, rotationY)
);


// Raycaster for mouse clicking
const mouse = new THREE.Vector2();
const clickRaycaster = new THREE.Raycaster();

// FreeRoam'da merkezdeki ⓘ ikonunun üzerine gelince ipucu göster
const hoverRaycaster = new THREE.Raycaster();
hoverRaycaster.far = 2; // Sadece 4 birim yakınında göster
let isHoveringIcon = false;


function checkIconHover() {
  if (!isFreeRoamActive() || !document.getElementById('infoBox').classList.contains('hidden') || isCinematic) {
    if (isHoveringIcon) {
      isHoveringIcon = false;
      const hint = document.getElementById('iconHint');
      if (hint) hint.classList.remove('visible');
    }
    return;
  }

  hoverRaycaster.setFromCamera({ x: 0, y: 0 }, currentCamera);
  const hits = hoverRaycaster.intersectObjects(clickableObjects);
  const hint = document.getElementById('iconHint');
  if (hits.length > 0) {
    if (!isHoveringIcon) {
      isHoveringIcon = true;
      if (hint) hint.classList.add('visible');
    }
  } else {
    if (isHoveringIcon) {
      isHoveringIcon = false;
      if (hint) hint.classList.remove('visible');
    }
  }
}

//Mouse click event
function handleFreeRoamInteraction() {
  if (!isFreeRoamActive()) return;

  mouse.x = 0;
  mouse.y = 0;

  clickRaycaster.setFromCamera(mouse, currentCamera);
  const intersects = clickRaycaster.intersectObjects(clickableObjects);

  if (intersects.length > 0) {
    const roomId = intersects[0].object.userData.roomId;
    showInfoBox(roomId);

    if (isMobile) {
      touchControls.disable();
    } else {
      controls.unlock();
    }
  }
}

// Masaüstünde fare tıklaması
window.addEventListener("click", (event) => {
  if (isMobile) return; // Mobilde TouchControls.onTap devrede
  handleFreeRoamInteraction();
});

function getTodayName() {
  const gunler = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
  return gunler[new Date().getDay()];
}
 
// Schedule tablosunu oluştur — HTML template'i klonlayarak DOM'u doldurur
// selectedSinif: null → tümü, number → o sınıf
// selectedGun: string → o gün
function buildScheduleTable(schedule, selectedSinif = null, selectedGun = null) {
  const gunSirasi = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

  // Bugün yoksa ilk günü seç
  const mevcutGunler = [...new Set(schedule.map(s => s.gun))]
    .sort((a, b) => gunSirasi.indexOf(a) - gunSirasi.indexOf(b));

  if (!selectedGun) {
    const bugun = getTodayName();
    selectedGun = mevcutGunler.includes(bugun) ? bugun : mevcutGunler[0];
  }

  // Sınıf filtresi + gün filtresi birlikte uygula
  const filtered = schedule.filter(s => {
    const gunEsit = s.gun === selectedGun;
    const sinifEsit = selectedSinif === null || s.sinif === selectedSinif;
    return gunEsit && sinifEsit;
  });

  const sorted = [...filtered].sort((a, b) => a.saat.localeCompare(b.saat));

  // HTML template'i klonla ve infoContent'e yerleştir
  const tpl = document.getElementById('scheduleTpl');
  const clone = tpl.content.cloneNode(true);

  // Gün butonlarını doldur
  const gunFilter = clone.getElementById('gunFilter');
  mevcutGunler.forEach(g => {
    const btn = document.createElement('button');
    btn.className = 'gun-btn' + (selectedGun === g ? ' active' : '');
    btn.textContent = g;
    btn.onclick = () => filterGun(g);
    gunFilter.appendChild(btn);
  });

  // Sınıf filtre butonlarını doldur
  const sinifFilter = clone.getElementById('sinifFilter');
  const tumBtn = document.createElement('button');
  tumBtn.className = 'sinif-btn' + (selectedSinif === null ? ' active' : '');
  tumBtn.textContent = 'Tümü';
  tumBtn.onclick = () => filterSinif(0);
  sinifFilter.appendChild(tumBtn);

  const siniflar = [...new Set(schedule.map(s => s.sinif))].sort();
  siniflar.forEach(s => {
    const btn = document.createElement('button');
    btn.className = 'sinif-btn' + (selectedSinif === s ? ' active' : '');
    btn.textContent = s + '. Sınıf';
    btn.onclick = () => filterSinif(s);
    sinifFilter.appendChild(btn);
  });

  // Tablo satırlarını doldur
  const tbody = clone.getElementById('scheduleBody');
  if (sorted.length > 0) {
    sorted.forEach(s => {
      const tr = document.createElement('tr');
      tr.innerHTML = `<td>${s.saat}</td><td>${s.ders}</td><td>${s.hoca}</td>`;
      tbody.appendChild(tr);
    });
  } else {
    const tr = document.createElement('tr');
    tr.innerHTML = `<td colspan="3" class="no-result">Bu gün için ders bulunmuyor.</td>`;
    tbody.appendChild(tr);
  }

  // infoContent'i temizle ve template içeriğini yerleştir
  const infoContent = document.getElementById('infoContent');
  infoContent.innerHTML = '';
  infoContent.appendChild(clone);
}

// Aktif açık olan oda id'sini tut (filtre butonları için)
let currentOpenRoomId = null;
let currentSelectedGun = null;
let currentSelectedSinif = null;
let currentSelectedMemberIndex = 0;
 
window.filterGun = function(gun) {
  currentSelectedGun = gun;
  const room = roomData[currentOpenRoomId];
  if (!room) return;
  buildScheduleTable(room.schedule, currentSelectedSinif, currentSelectedGun);
};
 
// Sınıf butonu tıklandığında (0 = Tümü)
window.filterSinif = function(sinif) {
  currentSelectedSinif = sinif === 0 ? null : Number(sinif);
  const room = roomData[currentOpenRoomId];
  if (!room) return;
  buildScheduleTable(room.schedule, currentSelectedSinif, currentSelectedGun);
};

// Akademisyen seçici — kişi tabına tıklandığında çağrılır
window.selectMember = function(index) {
  currentSelectedMemberIndex = index;
  const room = roomData[currentOpenRoomId];
  if (!room || room.type !== "akademisyen") return;
  buildAkademisyenPanel(room, index);
};

// Çoklu akademisyen pop-up'ı oluşturur
function buildAkademisyenPanel(room, selectedIndex = 0) {
  const infoContent = document.getElementById('infoContent');
  infoContent.innerHTML = '';

  const members = room.members;

  // Birden fazla kişi varsa üstte kişi seçici tablar göster
  if (members.length > 1) {
    const tabBar = document.createElement('div');
    tabBar.className = 'member-tab-bar';

    members.forEach((member, i) => {
      const btn = document.createElement('button');
      btn.className = 'member-tab-btn' + (i === selectedIndex ? ' active' : '');
      // Sadece kısa isim göster (örn. "Orhan SİVAZ")
      const shortName = member.name.replace(/^Arş\. Gör\.\s*/i, '').replace(/^Dr\.\s*/i, '').replace(/^Prof\.\s*Dr\.\s*/i, '');
      btn.textContent = shortName;
      btn.onclick = () => window.selectMember(i);
      tabBar.appendChild(btn);
    });

    infoContent.appendChild(tabBar);
  }

  // Seçili kişinin içeriğini göster
  const contentDiv = document.createElement('div');
  contentDiv.className = 'member-content';
  contentDiv.innerHTML = members[selectedIndex].content;
  infoContent.appendChild(contentDiv);
}

// Show the info box
function showInfoBox(roomId) {
 const room = roomData[roomId];
  if (!room) return;
 
  currentOpenRoomId = roomId;
  currentSelectedGun = null;
  currentSelectedSinif = null;
  currentSelectedMemberIndex = 0;
  document.getElementById('infoTitle').textContent = room.title;
  const infoBox = document.getElementById('infoBox');
 
  if (room.type === "schedule") {
    buildScheduleTable(room.schedule);
    document.getElementById('infoBox').classList.add('schedule');
    infoBox.classList.add('schedule');
    infoBox.classList.remove('akademisyen');
  } else if (room.type === "akademisyen") {
    buildAkademisyenPanel(room, 0);
    infoBox.classList.remove('schedule');
    infoBox.classList.add('akademisyen');
  } else {
    document.getElementById('infoContent').innerHTML = room.content;
    infoBox.classList.remove('schedule');
    infoBox.classList.remove('akademisyen');
  }
 
  infoBox.classList.remove('hidden');
  infoBox.classList.remove('shake');
  infoBox.classList.remove('fadein');
  void infoBox.offsetWidth;
  infoBox.classList.add('fadein');

  // Popup açıkken imleci göster
  document.body.style.cursor = 'default';

  // Hint'i gizle
  const hint = document.getElementById('iconHint');
  if (hint) hint.classList.remove('visible');
}

// Close button
document.getElementById('closeInfoBox').addEventListener('click', () => {
  document.getElementById('infoBox').classList.add('hidden');

  // Popup kapanınca imleci sıfırla (FreeRoam PointerLock zaten gizler)
  document.body.style.cursor = '';

  // EĞER MOBİLDESEK VE SERBEST GEZİNTİ KAMERASINDAYSAK KONTROLLERİ GERİ AÇ:
  if (isMobile && currentCamera === freeRoamCamera) {
    touchControls.enable();
  }

  // Hint'i hemen kontrol et (lock bekleme)
  hoverRaycaster.setFromCamera({ x: 0, y: 0 }, currentCamera);
  const hits = hoverRaycaster.intersectObjects(clickableObjects);
  const hint = document.getElementById('iconHint');
  if (hits.length > 0) {
    isHoveringIcon = true;
    if (hint) hint.classList.add('visible');
  } else {
    isHoveringIcon = false;
    if (hint) hint.classList.remove('visible');
  }

});

// Event Listeners for global window
window.addEventListener("resize", updateOnResize, false);
window.addEventListener("orientationchange", updateOnResize, false);

// Event Listeners for document
document.addEventListener("keydown", onKeyDown, false);
document.addEventListener("keyup", onKeyUp, false);


controls.addEventListener('unlock', () => {
  lastLockTime = Date.now();
  
  // Fare serbest bırakıldığında ekranda kalan "Bilgi için tıkla" yazısını temizle
  isHoveringIcon = false;
  const hint = document.getElementById('iconHint');
  if (hint) hint.classList.remove('visible');
});

document.addEventListener('pointerlockerror', (event) => {
  console.warn("Pointer lock hatası oluştu. (Hızlı giriş/çıkış yapıldı)");
  lastLockTime = Date.now();
});

document
  .querySelector("#changeToFreeRoamCamera")
  .addEventListener("click", changeToFreeRoamCamera);

document
  .querySelector("#changeToCinematicCamera")
  .addEventListener("click", changeToCinematicCamera);

document
  .querySelector("#startCinematicTrip")
  .addEventListener("click", startCinematicTrip);

currentCamera = freeRoamCamera;

animate();

initWelcomePopup({
  onClose: () => {
    if (isMobile) {
      touchControls.enable();
    }
  },
  onOpen: () => {
    if (isMobile) {
      touchControls.disable();
    }
  },
});

if (isMobile) {
  const hint = document.getElementById('iconHint');
  if (hint) hint.textContent = "👆 Bilgi için dokun";
}

setInterval(() => {
  if (controls.isLocked) {
    const pos = controls.getObject().position;
    const dir = new THREE.Vector3();
    controls.getDirection(dir);
    console.log(
  `coordinate: {\n` +
  `      x: ${pos.x.toFixed(1)},\n` +
  `      y: ${pos.y.toFixed(1)},\n` +
  `      z: ${pos.z.toFixed(1)},\n` +
  `    },\n` +
  `    lookAt: {\n` +
  `      x: ${dir.x.toFixed(1)},\n` +
  `      y: ${dir.y.toFixed(1)},\n` +
  `      z: ${dir.z.toFixed(1)},\n` +
  `    },`
);

  }
}, 1000);